import { z } from 'zod';
import {
    NOTIFICATION_CONDITION_OPERATORS,
    NOTIFICATION_FIELD_TYPES,
} from './notification-condition.schema.js';
import {
    NOTIFICATION_AUDIENCE_SELECTOR_TYPES,
    NotificationAudienceExpressionSchema,
} from './notification-audience.schema.js';

export const NOTIFICATION_POLICY_CHANNELS = Object.freeze(['in-app', 'email', 'sms', 'push']);
export const NOTIFICATION_POLICY_PRIORITIES = Object.freeze(['low', 'normal', 'high', 'urgent']);
export const NOTIFICATION_DISPATCH_PRIORITY_RANK = Object.freeze({
    urgent: 1,
    high: 2,
    normal: 3,
    low: 4,
});
export const NOTIFICATION_POLICY_CRITICAL_PRIORITIES = Object.freeze(['urgent', 'high']);
/** Managed routing modes: `off` is the global emergency kill-switch. */
export const NOTIFICATION_ROUTING_MODES = Object.freeze(['off', 'active']);

export function notificationDispatchPriorityRank(priority) {
    return NOTIFICATION_DISPATCH_PRIORITY_RANK[priority] || NOTIFICATION_DISPATCH_PRIORITY_RANK.normal;
}

export function notificationDispatchQueueName(priority) {
    return NOTIFICATION_POLICY_CRITICAL_PRIORITIES.includes(priority)
        ? 'notifications-critical'
        : 'notifications-normal';
}

export const NOTIFICATION_DELIVERY_CLASSES = Object.freeze(['required', 'actionable', 'informational']);
export const NOTIFICATION_CUSTOMIZATION_LEVELS = Object.freeze(['locked', 'limited', 'full']);
export const NOTIFICATION_MANAGEMENT_STATUSES = Object.freeze(['managed', 'deferred']);
export const NOTIFICATION_MANAGEMENT_AREAS = Object.freeze([
    'workflow',
    'securityAccess',
    'sla',
    'ticketOperations',
    'incidentOperations',
    'bcmOperations',
    'definitions',
    'privacyCatalog',
    'assetInventory',
    'incidentCrud',
    'generalCrud',
]);
export const NOTIFICATION_MANAGEMENT_KINDS = Object.freeze(['actionRequired', 'importantState', 'criticalCrud', 'historical']);
export const NOTIFICATION_MANAGEMENT_PRIORITIES = Object.freeze(['low', 'normal', 'high', 'urgent']);
export const NOTIFICATION_LIMITED_OVERRIDE_FIELDS = Object.freeze([
    'condition',
    'audience',
    'channels',
    'deliveryClass',
    'priority',
    'recipientCap',
    'contentSource',
]);
export const NOTIFICATION_RECIPIENT_CAP_OVERRIDE_RULES = Object.freeze(['atOrBelowBase']);

export const NotificationLocalizedTextSchema = z.object({
    tr: z.string().min(1).max(240),
    en: z.string().min(1).max(240),
}).strict();

export const NotificationSafeValueSchema = z.union([
    z.string().max(512),
    z.number().finite(),
    z.boolean(),
    z.null(),
]);

export const NotificationSafeFieldDefinitionSchema = z.object({
    key: z.string().min(1).max(64).regex(/^[a-z][A-Za-z0-9]*$/),
    type: z.enum(NOTIFICATION_FIELD_TYPES),
    label: NotificationLocalizedTextSchema,
    description: NotificationLocalizedTextSchema.optional(),
    allowedOperators: z.array(z.enum(NOTIFICATION_CONDITION_OPERATORS)).min(1).max(12),
    enumValues: z.array(z.string().max(120)).max(50).optional(),
    nullable: z.boolean().default(false),
    sensitivity: z.enum(['public', 'internal']).default('internal'),
}).strict().superRefine((field, ctx) => {
    if (field.type === 'enum' && (!field.enumValues || field.enumValues.length === 0)) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['enumValues'],
            message: 'notificationEvent.enumValuesRequired',
        });
    }
    if (field.type !== 'enum' && field.enumValues) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['enumValues'],
            message: 'notificationEvent.enumValuesForbidden',
        });
    }
});

export const NotificationEventVariableDefinitionSchema = z.object({
    key: z.string().min(1).max(64).regex(/^[a-z][A-Za-z0-9]*$/),
    type: z.enum(['string', 'number', 'boolean', 'date']),
    label: NotificationLocalizedTextSchema,
    description: NotificationLocalizedTextSchema.optional(),
    example: NotificationSafeValueSchema.optional(),
    required: z.boolean().default(true),
    maxLength: z.number().int().positive().max(2_000).optional(),
}).strict();

export const NotificationEventRelationDefinitionSchema = z.object({
    key: z.string().min(1).max(64).regex(/^[a-z][A-Za-z0-9]*$/),
    label: NotificationLocalizedTextSchema,
    description: NotificationLocalizedTextSchema,
    maxMembers: z.number().int().positive().max(1_000),
}).strict();

const externalRecipientKindSchema = z.string().min(1).max(80).regex(/^[a-z][a-zA-Z0-9._-]*$/);

export const NotificationEventContextSchema = z.object({
    version: z.number().int().positive(),
    event: z.object({
        id: z.string().min(1).max(160),
        name: z.string().min(1).max(160),
        occurredAt: z.string().datetime(),
        actorId: z.string().min(1).max(128).nullable(),
        correlationId: z.string().min(1).max(160),
        causationId: z.string().min(1).max(160).nullable(),
    }).strict(),
    actor: z.object({
        id: z.string().min(1).max(128),
        displayName: z.string().min(1).max(240),
    }).strict().optional(),
    tenant: z.object({ id: z.string().min(1).max(128) }).strict(),
    resource: z.object({
        type: z.string().min(1).max(80),
        id: z.string().min(1).max(128),
        pageKey: z.string().min(1).max(160),
    }).strict(),
    fields: z.record(z.string(), NotificationSafeValueSchema),
    relations: z.record(z.string(), z.array(z.string().min(1).max(128)).max(1_000)),
    variables: z.record(z.string(), NotificationSafeValueSchema),
    externalRecipients: z.array(z.object({
        kind: externalRecipientKindSchema,
        resourceId: z.string().min(1).max(128),
    }).strict()).max(1_000).default([]),
    navigation: z.object({
        pageKey: z.string().min(1).max(160),
        entityId: z.string().min(1).max(128).nullable(),
        kind: z.enum(['listRowHighlight', 'listPageOnly', 'detailTabHighlight']),
        tab: z.string().min(1).max(80).optional(),
    }).strict(),
}).strict();

const defaultContentSchema = z.object({
    mode: z.literal('eventDefault'),
    type: z.enum(['success', 'info', 'warning', 'error']),
}).strict();

export const NotificationEventDefinitionSchema = z.object({
    name: z.string().min(3).max(160).regex(/^[a-z][A-Za-z0-9]*(?:\.[a-z][A-Za-z0-9]*)+$/),
    version: z.number().int().positive(),
    label: NotificationLocalizedTextSchema,
    description: NotificationLocalizedTextSchema,
    module: z.string().min(1).max(80),
    category: z.string().min(1).max(80),
    notificationCategory: z.string().min(1).max(80),
    management: z.object({
        status: z.enum(NOTIFICATION_MANAGEMENT_STATUSES),
        area: z.enum(NOTIFICATION_MANAGEMENT_AREAS),
        kind: z.enum(NOTIFICATION_MANAGEMENT_KINDS),
        priority: z.enum(NOTIFICATION_MANAGEMENT_PRIORITIES),
    }).strict(),
    customization: z.enum(NOTIFICATION_CUSTOMIZATION_LEVELS),
    allowTenantSuppression: z.boolean().default(false),
    limitedOverrideFields: z.array(z.enum(NOTIFICATION_LIMITED_OVERRIDE_FIELDS)).max(7).default([]),
    limitedOverrideRules: z.object({
        recipientCap: z.enum(NOTIFICATION_RECIPIENT_CAP_OVERRIDE_RULES).optional(),
    }).strict().default({}),
    contextAdapterKey: z.string().min(1).max(120),
    accessGuardKey: z.string().min(1).max(120),
    navigationKind: z.enum(['listRowHighlight', 'listPageOnly']).optional(),
    verificationSourceTypes: z.array(z.enum(['synthetic', 'record', 'dispatch'])).min(1).max(3)
        .default(['synthetic', 'record', 'dispatch']),
    resource: z.object({
        type: z.string().min(1).max(80),
        pageKey: z.string().min(1).max(160),
    }).strict(),
    navigationProfiles: z.record(z.string(), z.object({ pageKey: z.string().min(1).max(160), category: z.string().min(1).max(80) }).strict()).optional(),
    detail: z.object({
        tab: z.string().min(1).max(80),
        maxLinkedIds: z.number().int().positive().max(500).default(500),
        roleHolderRecipients: z.boolean().optional(),
    }).strict().optional(),
    producer: z.object({
        kind: z.enum(['baseController', 'explicit']),
        source: z.string().min(1).max(240),
        resourceName: z.string().min(1).max(80).optional(),
        durable: z.boolean(),
    }).strict(),
    safeFields: z.array(NotificationSafeFieldDefinitionSchema).max(30),
    relations: z.array(NotificationEventRelationDefinitionSchema).max(20),
    audienceSelectors: z.array(z.enum(NOTIFICATION_AUDIENCE_SELECTOR_TYPES)).min(1).max(7),
    variables: z.array(NotificationEventVariableDefinitionSchema).min(1).max(30),
    runtimeOnlyVariables: z.array(z.string().min(1).max(64).regex(/^[a-z][A-Za-z0-9]*$/)).max(10).default([]),
    channelPolicy: z.object({
        required: z.array(z.enum(NOTIFICATION_POLICY_CHANNELS)).max(4),
        allowed: z.array(z.enum(NOTIFICATION_POLICY_CHANNELS)).min(1).max(4),
    }).strict(),
    externalRecipientKinds: z.array(externalRecipientKindSchema).max(4).default([]),
    externalChannels: z.array(z.enum(NOTIFICATION_POLICY_CHANNELS)).max(4).default([]),
    defaultPolicy: z.object({
        audience: NotificationAudienceExpressionSchema,
        content: defaultContentSchema,
        channels: z.array(z.enum(NOTIFICATION_POLICY_CHANNELS)).min(1).max(4),
        deliveryClass: z.enum(NOTIFICATION_DELIVERY_CLASSES),
        priority: z.enum(NOTIFICATION_POLICY_PRIORITIES),
        recipientCap: z.number().int().positive().max(1_000),
    }).strict(),
    maxRecipients: z.number().int().positive().max(1_000),
    previewFixture: NotificationEventContextSchema,
}).strict().superRefine((definition, ctx) => {
    if (definition.defaultPolicy.recipientCap > definition.maxRecipients) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['defaultPolicy', 'recipientCap'],
            message: 'notificationEvent.recipientCapExceedsDefinition',
        });
    }
    if (definition.externalRecipientKinds.length !== definition.externalChannels.length
        && (definition.externalRecipientKinds.length === 0 || definition.externalChannels.length === 0)) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['externalChannels'],
            message: 'notificationEvent.externalRecipientChannelsRequired',
        });
    }
    for (const channel of definition.externalChannels) {
        if (!definition.channelPolicy.allowed.includes(channel)) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ['externalChannels'],
                message: 'notificationEvent.externalChannelNotAllowed',
            });
            break;
        }
    }
    if (definition.customization === 'limited' && definition.limitedOverrideFields.length === 0) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['limitedOverrideFields'],
            message: 'notificationEvent.limitedOverridesRequired',
        });
    }
    if (definition.customization !== 'limited' && definition.limitedOverrideFields.length > 0) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['limitedOverrideFields'],
            message: 'notificationEvent.limitedOverridesUnexpected',
        });
    }
    const recipientCapOverridable = definition.limitedOverrideFields.includes('recipientCap');
    if (recipientCapOverridable && definition.limitedOverrideRules.recipientCap !== 'atOrBelowBase') {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['limitedOverrideRules', 'recipientCap'],
            message: 'notificationEvent.recipientCapOverrideRuleRequired',
        });
    }
    if (!recipientCapOverridable && definition.limitedOverrideRules.recipientCap) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['limitedOverrideRules', 'recipientCap'],
            message: 'notificationEvent.recipientCapOverrideRuleUnexpected',
        });
    }

    for (const [path, values] of [
        [['safeFields'], definition.safeFields.map((field) => field.key)],
        [['relations'], definition.relations.map((relation) => relation.key)],
        [['audienceSelectors'], definition.audienceSelectors],
        [['variables'], definition.variables.map((variable) => variable.key)],
        [['channelPolicy', 'required'], definition.channelPolicy.required],
        [['channelPolicy', 'allowed'], definition.channelPolicy.allowed],
        [['defaultPolicy', 'channels'], definition.defaultPolicy.channels],
    ]) {
        if (new Set(values).size !== values.length) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path,
                message: 'notificationEvent.duplicateEntry',
            });
        }
    }

    const allowedChannels = new Set(definition.channelPolicy.allowed);
    for (const channel of definition.channelPolicy.required) {
        if (!allowedChannels.has(channel)) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ['channelPolicy', 'required'],
                message: 'notificationEvent.requiredChannelNotAllowed',
            });
        }
        if (!definition.defaultPolicy.channels.includes(channel)) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ['defaultPolicy', 'channels'],
                message: 'notificationEvent.requiredChannelMissingFromDefaults',
            });
        }
    }
    for (const channel of definition.defaultPolicy.channels) {
        if (!allowedChannels.has(channel)) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ['defaultPolicy', 'channels'],
                message: 'notificationEvent.defaultChannelNotAllowed',
            });
        }
    }

    const relationKeys = new Set(definition.relations.map((relation) => relation.key));
    for (const [bucket, selectors] of Object.entries(definition.defaultPolicy.audience)) {
        selectors.forEach((selector, index) => {
            if (selector.type === 'eventRelation' && !relationKeys.has(selector.relation)) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    path: ['defaultPolicy', 'audience', bucket, index, 'relation'],
                    message: 'notificationEvent.unknownRelation',
                });
            }
        });
    }

    const safeFieldKeys = new Set(definition.safeFields.map((field) => field.key));
    for (const key of Object.keys(definition.previewFixture.fields)) {
        if (!safeFieldKeys.has(key)) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ['previewFixture', 'fields', key],
                message: 'notificationEvent.fixtureUnsafeField',
            });
        }
    }
    const variableKeys = new Set(definition.variables.map((variable) => variable.key));
    for (const key of Object.keys(definition.previewFixture.variables)) {
        if (!variableKeys.has(key)) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ['previewFixture', 'variables', key],
                message: 'notificationEvent.fixtureUnknownVariable',
            });
        }
    }
    for (const variable of definition.variables) {
        if (variable.required && !Object.hasOwn(definition.previewFixture.variables, variable.key)) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ['previewFixture', 'variables', variable.key],
                message: 'notificationEvent.fixtureMissingVariable',
            });
        }
    }

    if (definition.previewFixture.event.name !== definition.name) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['previewFixture', 'event', 'name'],
            message: 'notificationEvent.fixtureEventMismatch',
        });
    }
    if (definition.previewFixture.version !== definition.version) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['previewFixture', 'version'],
            message: 'notificationEvent.fixtureVersionMismatch',
        });
    }
    if (definition.previewFixture.resource.pageKey !== definition.resource.pageKey
        || definition.previewFixture.resource.type !== definition.resource.type) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['previewFixture', 'resource'],
            message: 'notificationEvent.fixtureResourceMismatch',
        });
    }
});
